/*
 * 单张图片预览
 *
 * aiClerk 内部三处图片大图查看的统一组件：
 * - 左侧文档预览的"查看原图"
 * - 聊天里 photo-list 块
 * - 聊天里 image-gallery 块
 *
 * 能力：缩放（滚轮 / 双击 / 按钮）、拖动平移、旋转、下载、ESC 关闭
 */
import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { message } from 'antd'
import {
  CloseOutlined,
  DownloadOutlined,
  RedoOutlined,
  ReloadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined
} from '@ant-design/icons'
import './ImagePreview.scss'

interface ImagePreviewProps {
  /** 图片 URL；传 null / '' 表示关闭 */
  src: string | null
  /** 图片描述，下载时也用作文件名 fallback */
  alt?: string
  onClose: () => void
}

const MIN_SCALE = 0.1
const MAX_SCALE = 10
const ZOOM_STEP = 0.2

export const ImagePreview: React.FC<ImagePreviewProps> = ({
  src,
  alt = '预览',
  onClose
}) => {
  const [scale, setScale] = useState(1)
  const [rotate, setRotate] = useState(0)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const [dragging, setDragging] = useState(false)
  const dragStart = useRef({ x: 0, y: 0, px: 0, py: 0 })
  const containerRef = useRef<HTMLDivElement | null>(null)

  // src 切换 → 重置变换
  useEffect(() => {
    if (!src) return
    setScale(1)
    setRotate(0)
    setPos({ x: 0, y: 0 })
  }, [src])

  // ESC 关闭 + 锁定 body 滚动
  useEffect(() => {
    if (!src) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [src, onClose])

  // 滚轮缩放
  // React 的 onWheel 默认 passive，preventDefault 无效，改用原生监听
  useEffect(() => {
    const el = containerRef.current
    if (!el || !src) return
    const handler = (e: WheelEvent) => {
      e.preventDefault()
      const delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP
      setScale((s) => {
        const next = s + delta * s
        return Math.max(MIN_SCALE, Math.min(MAX_SCALE, +next.toFixed(2)))
      })
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [src])

  // 拖动平移
  const handleImgMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return
    e.stopPropagation()
    setDragging(true)
    dragStart.current = {
      x: e.clientX,
      y: e.clientY,
      px: pos.x,
      py: pos.y
    }
  }
  useEffect(() => {
    if (!dragging) return
    const onMove = (e: MouseEvent) => {
      setPos({
        x: dragStart.current.px + e.clientX - dragStart.current.x,
        y: dragStart.current.py + e.clientY - dragStart.current.y
      })
    }
    const onUp = () => setDragging(false)
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [dragging])

  const zoomIn = () =>
    setScale((s) => Math.min(MAX_SCALE, +(s + s * ZOOM_STEP).toFixed(2)))
  const zoomOut = () =>
    setScale((s) => Math.max(MIN_SCALE, +(s - s * ZOOM_STEP).toFixed(2)))
  const reset = () => {
    setScale(1)
    setRotate(0)
    setPos({ x: 0, y: 0 })
  }
  const rotateRight = () => setRotate((r) => (r + 90) % 360)

  // 双击切换 100% ↔ 200%（200% 是查看水印细节常用倍率）
  const handleDoubleClick = () => {
    if (scale === 1) setScale(2)
    else reset()
  }

  const handleDownload = async () => {
    if (!src) return
    try {
      const res = await fetch(src, { mode: 'cors' })
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const baseName = alt && alt !== '预览' ? alt : 'image'
      a.download = /\.[a-z0-9]+$/i.test(baseName) ? baseName : `${baseName}.png`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      // CORS 兜底：直接打开新标签让用户右键保存
      window.open(src, '_blank')
      message.info('已在新标签页打开，可右键另存为')
    }
  }

  // 只有点在背景（stage 自身）上才关闭；点在 img 上不关闭
  const handleStageClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!src) return null

  return ReactDOM.createPortal(
    <div ref={containerRef} className="aiClerk-image-preview">
      <button
        type="button"
        className="aiClerk-image-preview__close"
        onClick={onClose}
        aria-label="关闭"
      >
        <CloseOutlined />
      </button>

      <div className="aiClerk-image-preview__stage" onClick={handleStageClick}>
        <img
          src={src}
          alt={alt}
          draggable={false}
          className="aiClerk-image-preview__img"
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale}) rotate(${rotate}deg)`,
            cursor: dragging ? 'grabbing' : 'grab'
          }}
          onMouseDown={handleImgMouseDown}
          onDoubleClick={handleDoubleClick}
        />
      </div>

      <div
        className="aiClerk-image-preview__toolbar"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button type="button" onClick={zoomOut} aria-label="缩小" title="缩小">
          <ZoomOutOutlined />
        </button>
        <span className="aiClerk-image-preview__scale">
          {Math.round(scale * 100)}%
        </span>
        <button type="button" onClick={zoomIn} aria-label="放大" title="放大">
          <ZoomInOutlined />
        </button>
        <span className="aiClerk-image-preview__divider" />
        <button type="button" onClick={reset} aria-label="重置" title="重置">
          <ReloadOutlined />
        </button>
        <button
          type="button"
          onClick={rotateRight}
          aria-label="旋转"
          title="旋转 90°"
        >
          <RedoOutlined />
        </button>
        <button
          type="button"
          onClick={handleDownload}
          aria-label="下载"
          title="下载"
        >
          <DownloadOutlined />
        </button>
      </div>
    </div>,
    document.body
  )
}

export default ImagePreview
